import { CaretRightFilled } from '@ant-design/icons';
import styled from 'styled-components';

const HOVER_OPACITY = 0.85;

export const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1392px; // 1440px - (24px * 2)
  margin: 0 auto;

  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.15);
  border-radius: 16px;
  background: ${(props) => props.theme.colors.whiteSmoke};
`;

export const IFrame = styled.iframe<{ isVimeo?: boolean }>`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  display: block;
  border-radius: ${(props) => (props.isVimeo ? '0' : '16px')};
  border: ${(props) => (props.isVimeo ? '0' : 'none')};
`;

export const ThumbnailImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  display: block;
  border-radius: 20px;
  object-fit: cover;
`;

export const PreviewVideo = styled.video`
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  display: block;
  border-radius: 20px;
  object-fit: cover;
`;

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
  color: white;
  gap: 16px;
  padding: 24px 16px;
  border-radius: 20px;

  @media ${(props) => props.theme.device.mobileL} {
    padding: 32px 24px;
  }

  @media ${(props) => props.theme.screen.md} {
    padding: 48px 28px;
  }
`;

export const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;

  @media ${(props) => props.theme.screen.md} {
    justify-content: flex-start;
  }
`;

export const VideoControlContainer = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;

  @media ${(props) => props.theme.screen.md} {
    justify-content: flex-start;
    padding-left: 15%;
    flex: initial;
  }
`;

type HoverProps = {
  isHovering: boolean;
};

export const PlayButtonContainer = styled.div<HoverProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: ${(props) => (props.isHovering ? 'scale(1.15)' : 'scale(1)')};
  opacity: ${(props) => (props.isHovering ? HOVER_OPACITY : 1)};
  background-color: #cfa55d;
  border-radius: 50%;

  width: 64px;
  height: 64px;

  @media ${(props) => props.theme.device.mobileL} {
    width: 80px;
    height: 80px;
  }

  @media ${(props) => props.theme.device.tablet} {
    width: 100px;
    height: 100px;
  }
`;

export const PlayIcon = styled(CaretRightFilled)`
  font-size: 24px;
  padding-left: 6px;
  color: white;

  @media ${(props) => props.theme.device.mobileL} {
    font-size: 48px;
    padding-left: 8px;
  }

  @media ${(props) => props.theme.device.tablet} {
    font-size: 60px;
  }
`;

export const CtaText = styled.div<HoverProps>`
  font-family: ${(props) => props.theme.fontFamilies.vollkorn};
  font-weight: 400;
  line-height: 90%;
  text-align: left;
  font-size: 20px;

  padding: 12px 10px 10px;
  background-color: #f6f4f0e5;
  color: ${(props) => props.theme.colors.black};

  border-radius: 20px;
  transition: all 0.3s ease;
  opacity: ${(props) => (props.isHovering ? HOVER_OPACITY : 1)};
  transform: ${(props) =>
    props.isHovering ? 'translateY(-2px)' : 'translateY(0)'};

  @media ${(props) => props.theme.device.mobileL} {
    font-size: 24px;
    padding: 14px 10px 10px;
  }

  @media ${(props) => props.theme.screen.md} {
    font-size: 30px;
    padding: 16px 14px 14px;
  }
`;

export const VideoInfo = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.sfPro};
  font-weight: 400;
  line-height: 110%;
  text-align: center;
  color: white;
  font-size: 14px;

  @media ${(props) => props.theme.device.mobileL} {
    font-size: 16px;
  }

  @media ${(props) => props.theme.screen.md} {
    font-size: 20px;
  }
`;

export const BrandSection = styled.div`
  display: none;
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  text-align: left;
  max-width: 600px;

  @media ${(props) => props.theme.screen.md} {
    display: block;
    bottom: 32px;
    left: 32px;
    right: 32px;
    max-width: none;
  }
`;

export const BrandTitle = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.vollkorn};
  font-weight: 700;
  font-size: 20px;
  line-height: 150%;
  color: #ffffffcc;
  margin-bottom: 4px;
  text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000,
    1px 1px 0 #000;

  @media ${(props) => props.theme.screen.lg} {
    font-size: 32px;
  }
`;

export const BrandDescription = styled.div`
  font-family: ${(props) => props.theme.fontFamilies.vollkorn};
  font-weight: 600;
  font-size: 12px;
  line-height: 130%;
  color: #ffffffcc;
  text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000,
    1px 1px 0 #000; // Black stroke effect

  @media ${(props) => props.theme.screen.lg} {
    font-size: 20px;
  }
`;
